/*
 * Copyright (C) 2018 - 2025. Entgra (Pvt) Ltd, https://entgra.io
 * All Rights Reserved.
 *
 * Unauthorized copying/redistribution of this file, via any medium
 * is strictly prohibited.
 * Proprietary and confidential.
 *
 * Licensed under the Entgra Commercial License,
 * Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 *
 * You may obtain a copy of the License at
 * https://entgra.io/licenses/entgra-commercial/1.0
 */

import React from 'react';
import * as serviceWorker from './services/serviceWorkers/serviceWorker';
import App from './App';
import Login from './scenes/Login';
import Home from './scenes/Home';
import './index.css';
import config from '../public/conf/config.json';
import DeviceTypes from './scenes/Home/scenes/DeviceTypes';
import DeviceOrganizationView from './scenes/Home/scenes/DeviceOrganizationView';
import AnalyticReports from './scenes/Home/scenes/AnalyticReports';
import HourlyAnalyticReports from './scenes/Home/scenes/HourlyAnalyticReports';
import DailyAnalyticReports from './scenes/Home/scenes/DailyAnalyticReports';
import DailyEnergyConsumptionReports from './scenes/Home/scenes/DailyEnergyConsumptionReports';
import DailyPowerQualityReports from './scenes/Home/scenes/DailyPowerQualityReports';
import MeterData from './scenes/Home/scenes/MeterData';
import ProfileData from './scenes/Home/scenes/ProfileData';
import ProfileHistoricalReading from './scenes/Home/scenes/ProfileHistoricalReading';
import MeterView from './scenes/Home/scenes/MeterView';
import OtherDevicesTemperature from './scenes/Home/scenes/OtherDevicesTemperature';
import OtherDevicesUPS from './scenes/Home/scenes/OtherDevicesUPS';
import MeterInventory from './scenes/Home/scenes/MeterInventory';
import OtherDevicesHistory from './scenes/Home/scenes/OtherDevicesHistory';
import MeterAssignment from './scenes/Home/scenes/MeterAssignment';
import MeterReadingsHistorical from './scenes/Home/scenes/MeterReadingsHistorical';
import MeterReadingsCurrent from './scenes/Home/scenes/MeterReadingsCurrent';
import MeterReading from './scenes/Home/scenes/MeterReading';
import OperationResponse from './scenes/Home/scenes/OperationResponse';
import HomeDashboard from './scenes/Home/scenes/Home-Dashboard';
import WeeklyPowerConsumptionReports from './scenes/Home/scenes/WeeklyPowerConsumptionReports';
import MonthlyPowerConsumptionReports from './scenes/Home/scenes/MonthlyPowerConsumptionReports';
import store from './app/store';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import Localizer from './components/Localizer';

const root = createRoot(document.getElementById('root'));
const routes = [
  {
    path: `/${config.appName}/login`,
    exact: true,
    component: Login
  },
  {
    path: `/${config.appName}`,
    exact: false,
    component: Home,
    routes: [
      {
        path: `/${config.appName}/home`,
        component: HomeDashboard,
        exact: true
      },
      {
        path: `/${config.appName}/device-organization/view`,
        component: DeviceOrganizationView,
        exact: true
      },
      {
        path: `/${config.appName}/facility/assignment`,
        component: MeterAssignment,
        exact: true
      },
      {
        path: `/${config.appName}/other/:deviceType/:deviceId`,
        component: OtherDevicesHistory,
        exact: true
      },
      {
        path: `/${config.appName}/other/temperature`,
        component: OtherDevicesTemperature,
        exact: true
      },
      {
        path: `/${config.appName}/other/ups`,
        component: OtherDevicesUPS,
        exact: true
      },
      {
        path: `/${config.appName}/meter/inventory`,
        component: MeterInventory,
        exact: true
      },
      {
        path: `/${config.appName}/meter/view`,
        component: MeterView,
        exact: true
      },
      {
        path: `/${config.appName}/meter/data`,
        component: MeterData,
        exact: true
      },
      {
        path: `/${config.appName}/load/profile-data`,
        component: ProfileData,
        exact: true
      },
      {
        path: `/${config.appName}/reports/raw-data-analytics`,
        component: AnalyticReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/hourly-data-analytics`,
        component: HourlyAnalyticReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/daily-data-analytics`,
        component: DailyAnalyticReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/daily-energy-consumption-analytics`,
        component: DailyEnergyConsumptionReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/daily-power-quality-analytics`,
        component: DailyPowerQualityReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/monthly-power-consumption-analytics`,
        component: MonthlyPowerConsumptionReports,
        exact: true
      },
      {
        path: `/${config.appName}/reports/weekly-power-consumption-analytics`,
        component: WeeklyPowerConsumptionReports,
        exact: true
      },
      {
        path: `/${config.appName}/load/historical`,
        component: ProfileHistoricalReading,
        exact: true
      },
      {
        path: `/${config.appName}/read/current`,
        component: MeterReadingsCurrent,
        exact: true
      },
      {
        path: `/${config.appName}/read/historical`,
        component: MeterReadingsHistorical,
        exact: true
      },
      {
        path: `/${config.appName}/read/operations/readings`,
        component: MeterReading,
        exact: true
      },
      {
        path: `/${config.appName}/read/operations/activities`,
        component: OperationResponse,
        exact: true
      },
      {
        path: `/${config.appName}/devices/types`,
        component: DeviceTypes,
        exact: true
      },
    ]
  }
];

root.render(
  <Provider store={store}>
    <Localizer>
    <App routes={routes} />
    </Localizer>
  </Provider>
);

// If you want your app e and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
